import { JSXElementConstructor, ReactElement, useState } from 'react';
import { AutocompleteRenderInputParams, TextField } from '@hopskipdrive/react-components';
import { PopperProps } from '@mui/material/Popper';

import { useGetClassifications } from '../../hooks/useIncidentApi';
import { IncidentClassification } from '../../types/incidents';

import {
  IncidentItem,
  StyledAutocomplete,
  StyledIncidentClassification,
  StyledIncidentPriority,
} from './IncidentClassificationDropdown.styles';

export interface IncidentClassificationDropdownProps {
  options?: IncidentClassification[]; // This is only used in stories, can be removed if not needed
  value: IncidentClassification | null;
  onChange: (value: IncidentClassification | null) => void;
  open?: boolean;
  error?: boolean;
  helperText?: string;
  popper?: JSXElementConstructor<PopperProps>;
}

const IncidentClassificationDropdown = ({
  value,
  onChange,
  open,
  error,
  helperText,
  popper,
}: IncidentClassificationDropdownProps): ReactElement => {
  const { isFetching, data: response } = useGetClassifications({
    subclassifications_count: true,
  });

  const [inputValue, setInputValue] = useState<string>('');

  const filterOptionsOnSearch = (options) => {
    if (!value && inputValue) {
      const lowercasedSearchString = inputValue?.toLowerCase();

      let stringMatch = false;

      options = options.filter((option) => {
        stringMatch = option.name.toLowerCase().includes(lowercasedSearchString);

        return stringMatch || lowercasedSearchString.includes(option.priority);
      });
    }

    return options;
  };

  return (
    <StyledAutocomplete
      options={response?.data || []}
      open={open}
      isLoading={isFetching}
      filterOptions={filterOptionsOnSearch}
      inputValue={inputValue}
      onInputChange={(_, input) => !value && setInputValue(input)}
      getOptionLabel={(_) => ''}
      value={value}
      PopperComponent={popper}
      onChange={(_, selected) => {
        onChange(selected || null);
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          variant="outlined"
          error={error}
          helperText={helperText}
          placeholder={value ? '' : 'Select Classification'}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && value) {
              onChange(null);
            }
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {params.InputProps.startAdornment}
                {value ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                    <StyledIncidentPriority variant="body2" marginRight={1} className={'p' + value.priority}>
                      {'p' + value.priority}
                    </StyledIncidentPriority>

                    <StyledIncidentClassification
                      word-wrap="break-word"
                      overflow-wrap="break-word"
                      className={value.name}
                    >
                      {value.name}
                    </StyledIncidentClassification>
                  </div>
                ) : null}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <IncidentItem {...props} key={option.name} className={value?.name === option.name ? 'selected' : ''}>
            <StyledIncidentPriority variant="body2" className={'menu p' + option.priority}>
              p{option.priority}
            </StyledIncidentPriority>
            <StyledIncidentClassification>{option.name}</StyledIncidentClassification>
          </IncidentItem>
        );
      }}
    />
  );
};

export default IncidentClassificationDropdown;
